import {
  Button,
  PhoneMockup,
  PhoneMockupBodyLayout,
  PhoneMockupLayout,
  Text,
  TextLink,
  WidgetHeader,
  WidgetHeaderContent,
  WidgetStatus,
} from '@frontend/ui-library';
import { getIdFromUrl } from '@frontend/utils';
import { useEffect } from 'react';

import {
  SatisfactionRating,
  TopBar,
  TransactionDropdown,
  addStatusToTransactions,
} from '../../components';
import links from '../../constants/links';
import { useWidgetContext } from '../../context/WidgetContext';

import { PageFields_OverpaidPage_Fragment } from '../../graphql/generated';
import { useRegisterHotjarPage } from '../../hooks';
import { isOfframpBill } from '../../utils';

export interface PaymentOverpaidProps {
  data: PageFields_OverpaidPage_Fragment['pageData'];
}

const PaymentOverpaid = ({ data }: PaymentOverpaidProps) => {
  const {
    paidAmount,
    expectedAmount,
    refundAmount,
    confirmedTransactions,
    merchantName,
    returnUrl,
  } = data;

  const cryptoCurrency = data.cryptoCurrency.toUpperCase();

  const { configureHeader } = useWidgetContext();
  useRegisterHotjarPage('payment-overpaid');

  useEffect(() => {
    configureHeader({
      id: getIdFromUrl(),
      merchantName,
      billType: data.billType,
    });
  }, []);

  const isOffRamp = isOfframpBill(data.billType);

  return (
    <PhoneMockupLayout>
      <PhoneMockup
        header={
          <WidgetHeader color="success">
            <WidgetHeaderContent title="Success" />
          </WidgetHeader>
        }
      >
        <PhoneMockupBodyLayout
          header={
            <TopBar
              rightSlot={
                <TransactionDropdown
                  transactions={addStatusToTransactions(
                    confirmedTransactions,
                    'confirmed'
                  )}
                />
              }
            />
          }
          bodyCentered
          body={
            <>
              <WidgetStatus
                emoji="🐋"
                title={`You ${isOffRamp ? 'over transferred' : 'overpaid'}`}
                description={
                  isOffRamp ? (
                    <>
                      You transferred{' '}
                      <strong>
                        {paidAmount} {cryptoCurrency}
                      </strong>{' '}
                      for a total off ramp of{' '}
                      <strong>
                        {expectedAmount} {cryptoCurrency}.
                      </strong>
                    </>
                  ) : (
                    <>
                      You paid{' '}
                      <strong>
                        {paidAmount} {cryptoCurrency}
                      </strong>{' '}
                      for a{' '}
                      <strong>
                        {expectedAmount} {cryptoCurrency}
                      </strong>{' '}
                      purchase.
                    </>
                  )
                }
              />
              <Text
                as="div"
                align="center"
                mt="8"
                color="royal-500"
                size="small"
              >
                You will be refunded{' '}
                <strong>
                  {refundAmount} {cryptoCurrency}.
                </strong>{' '}
                Keep an eye on your email for refund instructions.
              </Text>
              <SatisfactionRating rating={data.rating || undefined} />
            </>
          }
          footer={
            <>
              <Text size="tiny" color="neutral-500" align="center">
                Didn’t get the email? Please check your spam folder. If you
                still don’t see it, please reach out to{' '}
                <TextLink
                  size="tiny"
                  href={links.support.HOME}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  our support.
                </TextLink>
              </Text>
              {returnUrl && (
                <Button
                  as="a"
                  href={returnUrl}
                  mt="16"
                  size="large"
                  variant="secondary"
                >
                  Go back to store
                </Button>
              )}
            </>
          }
        />
      </PhoneMockup>
    </PhoneMockupLayout>
  );
};

export default PaymentOverpaid;
